import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import 'leaflet.heat';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../shared/context/AuthContext';
import api from '../../../shared/lib/api';
import { Button } from '../../../shared/components/ui/button';
import { Input } from '../../../shared/components/ui/input';
import { ArrowLeft, MapPin, Users, Search, RefreshCw, Building2, Flame, Layers, Filter, X, Check, RotateCcw } from 'lucide-react';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons
const createCustomIcon = (color, hasActivePersonnel = false) => {
  const size = hasActivePersonnel ? 35 : 25;
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        background-color: ${color};
        width: ${size}px;
        height: ${size}px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        border: 3px solid white;
        box-shadow: 0 2px 5px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        ${hasActivePersonnel ? `
          <div style="
            transform: rotate(45deg);
            color: white;
            font-weight: bold;
            font-size: 12px;
          ">●</div>
        ` : ''}
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size],
  });
};

// Custom cluster icon
const createClusterCustomIcon = (cluster) => {
  const count = cluster.getChildCount();
  let size = 40;
  let bgColor = '#3b82f6'; // blue
  
  if (count > 100) {
    size = 55;
    bgColor = '#ef4444'; // red for large clusters
  } else if (count > 50) {
    size = 50;
    bgColor = '#f97316'; // orange for medium clusters  
  } else if (count > 20) {
    size = 45;
    bgColor = '#eab308'; // yellow for small-medium
  }
  
  return L.divIcon({
    html: `
      <div style="
        background-color: ${bgColor};
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        font-size: ${count > 100 ? '14px' : '12px'};
        border: 3px solid white;
        box-shadow: 0 3px 10px rgba(0,0,0,0.3);
      ">
        ${count}
      </div>
    `,
    className: 'custom-cluster-icon',
    iconSize: L.point(size, size, true),
  });
};

// Component to fit map bounds
function FitBounds({ stations }) {
  const map = useMap();
  
  useEffect(() => {
    if (stations.length > 0) {
      const bounds = stations
        .filter(s => s.gps_lat && s.gps_lng)
        .map(s => [s.gps_lat, s.gps_lng]);
      
      if (bounds.length > 0) {
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [stations, map]);
  
  return null;
}

// Heatmap Layer Component
function HeatmapLayer({ stations, show }) {
  const map = useMap();
  
  useEffect(() => {
    if (!show || stations.length === 0) return;
    
    // Create heatmap data points [lat, lng, intensity]
    const heatData = stations
      .filter(s => s.gps_lat && s.gps_lng)
      .map(s => [s.gps_lat, s.gps_lng, 1]);
    
    // Create heatmap layer
    const heat = L.heatLayer(heatData, {
      radius: 25,
      blur: 15,
      maxZoom: 12,
      max: 1.0,
      gradient: {
        0.0: '#3b82f6',  // blue
        0.25: '#22d3ee', // cyan
        0.5: '#22c55e',  // green
        0.75: '#eab308', // yellow
        1.0: '#ef4444'   // red
      }
    });
    
    heat.addTo(map);
    
    return () => {
      map.removeLayer(heat);
    };
  }, [map, stations, show]);
  
  return null;
}

export default function MapView() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stations, setStations] = useState([]);
  const [activeVisits, setActiveVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showOnlyWithPersonnel, setShowOnlyWithPersonnel] = useState(false);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [viewMode, setViewMode] = useState('markers'); // 'markers' | 'heatmap' | 'both'
  const [selectedRegions, setSelectedRegions] = useState([]); // Empty = all regions
  const [showRegionFilter, setShowRegionFilter] = useState(false);
  const [selectedOffices, setSelectedOffices] = useState([]); // Empty = all offices
  const [showOfficeFilter, setShowOfficeFilter] = useState(false);

  // Fetch stations and active visits
  const fetchData = async () => {
    setLoading(true);
    try {
      const [stationsRes, visitsRes] = await Promise.all([
        api.get('/stations?include_inactive=false'),
        api.get('/visits/active')
      ]);
      
      setStations(stationsRes.data || []);
      setActiveVisits(visitsRes.data || []);
    } catch (error) {
      console.error('Error fetching map data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  // Create a map of station_id to active personnel count
  const personnelByStation = useMemo(() => {
    const map = {};
    activeVisits.forEach(visit => {
      if (visit.station_id) {
        if (!map[visit.station_id]) {
          map[visit.station_id] = [];
        }
        map[visit.station_id].push(visit);
      }
    });
    return map;
  }, [activeVisits]);

  // Get available regions from station data
  const availableRegions = useMemo(() => {
    const regions = new Set();
    stations.forEach(s => {
      if (s.region) {
        regions.add(s.region.toUpperCase());
      }
    });
    return Array.from(regions).sort();
  }, [stations]);

  // Get available reporting offices from station data
  const availableOffices = useMemo(() => {
    const offices = new Set();
    stations.forEach(s => {
      if (s.reporting_office_name) {
        offices.add(s.reporting_office_name.toUpperCase());
      }
    });
    return Array.from(offices).sort();
  }, [stations]);

  // Toggle region selection
  const toggleRegion = (region) => {
    setSelectedRegions(prev => {
      if (prev.includes(region)) {
        return prev.filter(r => r !== region);
      } else {
        return [...prev, region];
      }
    });
  };

  // Select/deselect all regions
  const toggleAllRegions = () => {
    if (selectedRegions.length === availableRegions.length) {
      setSelectedRegions([]);
    } else {
      setSelectedRegions([...availableRegions]);
    }
  };

  // Toggle office selection
  const toggleOffice = (office) => {
    setSelectedOffices(prev => {
      if (prev.includes(office)) {
        return prev.filter(o => o !== office);
      } else {
        return [...prev, office];
      }
    });
  };

  // Select/deselect all offices
  const toggleAllOffices = () => {
    if (selectedOffices.length === availableOffices.length) {
      setSelectedOffices([]);
    } else {
      setSelectedOffices([...availableOffices]);
    }
  };

  // Clear all filters
  const clearAllFilters = () => {
    setSearchQuery('');
    setSelectedRegions([]);
    setSelectedOffices([]);
    setShowOnlyWithPersonnel(false);
  };

  // Check if any filter is active
  const hasActiveFilters = searchQuery || selectedRegions.length > 0 || selectedOffices.length > 0 || showOnlyWithPersonnel;

  // Filter stations with valid GPS coordinates
  const mappableStations = useMemo(() => {
    let filtered = stations.filter(s => s.gps_lat && s.gps_lng);
    
    // Filter by selected regions
    if (selectedRegions.length > 0) {
      filtered = filtered.filter(s => 
        s.region && selectedRegions.includes(s.region.toUpperCase())
      );
    }

    // Filter by selected offices
    if (selectedOffices.length > 0) {
      filtered = filtered.filter(s => 
        s.reporting_office_name && selectedOffices.includes(s.reporting_office_name.toUpperCase())
      );
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(s => 
        s.station_name?.toLowerCase().includes(query) ||
        s.station_code?.toLowerCase().includes(query) ||
        s.city?.toLowerCase().includes(query)
      );
    }
    
    if (showOnlyWithPersonnel) {
      filtered = filtered.filter(s => personnelByStation[s.id]?.length > 0);
    }
    
    return filtered;
  }, [stations, searchQuery, showOnlyWithPersonnel, personnelByStation, selectedRegions, selectedOffices]);

  // Stats
  const stats = useMemo(() => ({
    totalStations: stations.length,
    mappableStations: mappableStations.length,
    stationsWithPersonnel: Object.keys(personnelByStation).length,
    totalActivePersonnel: activeVisits.length
  }), [stations, mappableStations, personnelByStation, activeVisits]);

  // Default center (Illinois)
  const defaultCenter = [41.8781, -87.6298];

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Header */}
      <div className="bg-zinc-900 border-b border-zinc-800 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="text-zinc-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-orange-500" />
              <h1 className="text-xl font-bold">Station Map</h1>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Stats */}
            <div className="hidden md:flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-blue-400" />
                <span className="text-zinc-400">Stations:</span>
                <span className="font-mono text-white">{stats.mappableStations}</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-green-400" />
                <span className="text-zinc-400">Active:</span>
                <span className="font-mono text-green-400">{stats.totalActivePersonnel}</span>
              </div>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={fetchData}
              disabled={loading}
              className="border-zinc-700"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-zinc-900/50 border-b border-zinc-800 px-4 py-3">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              placeholder="Search stations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
            />
          </div>

          {/* Region Filter */}
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setShowRegionFilter(!showRegionFilter); setShowOfficeFilter(false); }}
              className={`border-zinc-700 ${selectedRegions.length > 0 ? 'bg-green-500/20 border-green-500/50' : ''}`}
            >
              <Filter className="w-4 h-4 mr-2" />
              Regions
              {selectedRegions.length > 0 && (
                <span className="ml-1.5 bg-green-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {selectedRegions.length}
                </span>
              )}
            </Button>
            
            {/* Region Dropdown */}
            {showRegionFilter && (
              <div className="absolute top-full mt-2 left-0 z-50 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl min-w-[200px]">
                <div className="p-2 border-b border-zinc-700">
                  <button
                    onClick={toggleAllRegions}
                    className="w-full text-left px-3 py-2 text-sm rounded hover:bg-zinc-800 flex items-center justify-between"
                  >
                    <span className="text-zinc-300">
                      {selectedRegions.length === availableRegions.length ? 'Deselect All' : 'Select All'}
                    </span>
                    {selectedRegions.length === availableRegions.length && (
                      <Check className="w-4 h-4 text-green-500" />
                    )}
                  </button>
                </div>
                <div className="p-2 max-h-[300px] overflow-y-auto">
                  {availableRegions.map(region => {
                    const isSelected = selectedRegions.includes(region);
                    const regionCount = stations.filter(s => s.region?.toUpperCase() === region && s.gps_lat && s.gps_lng).length;
                    return (
                      <button
                        key={region}
                        onClick={() => toggleRegion(region)}
                        className={`w-full text-left px-3 py-2 text-sm rounded flex items-center justify-between transition-colors ${
                          isSelected ? 'bg-green-500/20 text-green-400' : 'hover:bg-zinc-800 text-zinc-300'
                        }`}
                      >
                        <span className="font-mono">{region}</span>
                        <span className="flex items-center gap-2">
                          <span className="text-xs text-zinc-500">{regionCount}</span>
                          {isSelected && <Check className="w-4 h-4 text-green-500" />}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {selectedRegions.length > 0 && (
                  <div className="p-2 border-t border-zinc-700">
                    <button
                      onClick={() => setSelectedRegions([])}
                      className="w-full text-left px-3 py-2 text-sm rounded hover:bg-zinc-800 flex items-center gap-2 text-red-400"
                    >
                      <X className="w-4 h-4" />
                      Clear Filters
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Office Filter */}
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setShowOfficeFilter(!showOfficeFilter); setShowRegionFilter(false); }}
              className={`border-zinc-700 ${selectedOffices.length > 0 ? 'bg-blue-500/20 border-blue-500/50' : ''}`}
            >
              <Building2 className="w-4 h-4 mr-2" />
              Offices
              {selectedOffices.length > 0 && (
                <span className="ml-1.5 bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {selectedOffices.length}
                </span>
              )}
            </Button>
            
            {/* Office Dropdown */}
            {showOfficeFilter && (
              <div className="absolute top-full mt-2 left-0 z-50 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl min-w-[250px]">
                <div className="p-2 border-b border-zinc-700">
                  <button
                    onClick={toggleAllOffices}
                    className="w-full text-left px-3 py-2 text-sm rounded hover:bg-zinc-800 flex items-center justify-between"
                  >
                    <span className="text-zinc-300">
                      {selectedOffices.length === availableOffices.length ? 'Deselect All' : 'Select All'}
                    </span>
                    {selectedOffices.length === availableOffices.length && (
                      <Check className="w-4 h-4 text-blue-500" />
                    )}
                  </button>
                </div>
                <div className="p-2 max-h-[300px] overflow-y-auto">
                  {availableOffices.map(office => {
                    const isSelected = selectedOffices.includes(office);
                    const officeCount = stations.filter(s => s.reporting_office_name?.toUpperCase() === office && s.gps_lat && s.gps_lng).length;
                    return (
                      <button
                        key={office}
                        onClick={() => toggleOffice(office)}
                        className={`w-full text-left px-3 py-2 text-sm rounded flex items-center justify-between transition-colors ${
                          isSelected ? 'bg-blue-500/20 text-blue-400' : 'hover:bg-zinc-800 text-zinc-300'
                        }`}
                      >
                        <span className="font-medium truncate max-w-[150px]">{office}</span>
                        <span className="flex items-center gap-2">
                          <span className="text-xs text-zinc-500">{officeCount}</span>
                          {isSelected && <Check className="w-4 h-4 text-blue-500" />}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {selectedOffices.length > 0 && (
                  <div className="p-2 border-t border-zinc-700">
                    <button
                      onClick={() => setSelectedOffices([])}
                      className="w-full text-left px-3 py-2 text-sm rounded hover:bg-zinc-800 flex items-center gap-2 text-red-400"
                    >
                      <X className="w-4 h-4" />
                      Clear Filters
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <Button
            variant={showOnlyWithPersonnel ? "default" : "outline"}
            size="sm"
            onClick={() => setShowOnlyWithPersonnel(!showOnlyWithPersonnel)}
            className={showOnlyWithPersonnel ? "bg-orange-500 hover:bg-orange-600" : "border-zinc-700"}
          >
            <Users className="w-4 h-4 mr-2" />
            Active Only
          </Button>

          {/* Clear All Filters Button */}
          {hasActiveFilters && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearAllFilters}
              className="border-red-500/50 bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Clear All
            </Button>
          )}

          {/* View Mode Toggle */}
          <div className="flex items-center border border-zinc-700 rounded-md overflow-hidden">
            <button
              onClick={() => setViewMode('markers')}
              className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 transition-colors ${
                viewMode === 'markers' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-zinc-800 text-zinc-400 hover:text-white'
              }`}
            >
              <Layers className="w-3 h-3" />
              Markers
            </button>
            <button
              onClick={() => setViewMode('heatmap')}
              className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 transition-colors ${
                viewMode === 'heatmap' 
                  ? 'bg-orange-500 text-white' 
                  : 'bg-zinc-800 text-zinc-400 hover:text-white'
              }`}
            >
              <Flame className="w-3 h-3" />
              Heatmap
            </button>
            <button
              onClick={() => setViewMode('both')}
              className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1.5 transition-colors ${
                viewMode === 'both' 
                  ? 'bg-purple-500 text-white' 
                  : 'bg-zinc-800 text-zinc-400 hover:text-white'
              }`}
            >
              Both
            </button>
          </div>

          {/* Legend */}
          <div className="hidden lg:flex items-center gap-4 text-xs text-zinc-400">
            {viewMode !== 'heatmap' && (
              <>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span>Station</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
                    <span className="text-white text-[8px]">●</span>
                  </div>
                  <span>Active</span>
                </div>
              </>
            )}
            {viewMode === 'heatmap' || viewMode === 'both' ? (
              <div className="flex items-center gap-1">
                <span className="text-zinc-500">Density:</span>
                <div className="flex h-3 rounded overflow-hidden">
                  <div className="w-4 bg-blue-500"></div>
                  <div className="w-4 bg-cyan-400"></div>
                  <div className="w-4 bg-green-500"></div>
                  <div className="w-4 bg-yellow-500"></div>
                  <div className="w-4 bg-red-500"></div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-1 border-l border-zinc-700 pl-4">
                <span className="text-zinc-500">Clusters:</span>
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="h-[calc(100vh-140px)]">
        {loading && mappableStations.length === 0 ? (
          <div className="h-full flex items-center justify-center bg-zinc-900">
            <div className="text-center">
              <RefreshCw className="w-8 h-8 text-orange-500 animate-spin mx-auto mb-4" />
              <p className="text-zinc-400">Loading station data...</p>
            </div>
          </div>
        ) : (
          <MapContainer
            center={defaultCenter}
            zoom={7}
            style={{ height: '100%', width: '100%' }}
            className="z-0"
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            <FitBounds stations={mappableStations} />
            
            {/* Heatmap Layer */}
            <HeatmapLayer 
              stations={mappableStations} 
              show={viewMode === 'heatmap' || viewMode === 'both'} 
            />
            
            {/* Marker Clusters (hidden in heatmap-only mode) */}
            {(viewMode === 'markers' || viewMode === 'both') && (
              <MarkerClusterGroup
                chunkedLoading
                iconCreateFunction={createClusterCustomIcon}
                maxClusterRadius={60}
                spiderfyOnMaxZoom={true}
                showCoverageOnHover={false}
                zoomToBoundsOnClick={true}
                disableClusteringAtZoom={15}
              >
                {mappableStations.map((station) => {
                  const activePersonnel = personnelByStation[station.id] || [];
                  const hasActivePersonnel = activePersonnel.length > 0;
                  const markerColor = hasActivePersonnel ? '#22c55e' : '#3b82f6';
                
                return (
                  <Marker
                    key={station.id}
                    position={[station.gps_lat, station.gps_lng]}
                    icon={createCustomIcon(markerColor, hasActivePersonnel)}
                  >
                    <Popup className="station-popup">
                      <div className="min-w-[250px]">
                        <div className="font-bold text-lg mb-1">{station.station_name}</div>
                        {station.station_code && (
                          <div className="text-xs text-gray-500 font-mono mb-2">{station.station_code}</div>
                        )}
                        
                        <div className="text-sm text-gray-600 mb-3">
                          {station.street_address && <div>{station.street_address}</div>}
                          <div>
                            {station.city}{station.city && station.state ? ', ' : ''}{station.state} {station.zip_code}
                          </div>
                          {station.region && <div className="text-xs mt-1">Region: {station.region}</div>}
                        </div>
                        
                        {hasActivePersonnel ? (
                          <div className="bg-green-50 border border-green-200 rounded p-2 mb-2">
                            <div className="flex items-center gap-2 text-green-700 font-medium mb-1">
                              <Users className="w-4 h-4" />
                              {activePersonnel.length} Active Personnel
                            </div>
                            <div className="space-y-1">
                              {activePersonnel.map((visit) => (
                                <div key={visit.id} className="text-xs text-green-600">
                                  • {visit.user_name} ({visit.purpose})
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="text-xs text-gray-400 italic">No active personnel</div>
                        )}
                        
                        <div className="text-xs text-gray-400 mt-2">
                          GPS: {station.gps_lat?.toFixed(5)}, {station.gps_lng?.toFixed(5)}
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
              </MarkerClusterGroup>
            )}
          </MapContainer>
        )}
      </div>

      {/* Leaflet CSS override for dark theme */}
      <style>{`
        .leaflet-container {
          background: #18181b;
        }
        .leaflet-popup-content-wrapper {
          border-radius: 8px;
        }
        .leaflet-popup-content {
          margin: 12px;
        }
        .custom-marker {
          background: transparent !important;
          border: none !important;
        }
      `}</style>
    </div>
  );
}
